import React, { useEffect, useRef, useState } from "react";
import { Menu, X, Sun, Moon, Palette, Globe, Check } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import Crest from "./Crest";
import { useSite, ACCENTS } from "../context/SiteContext";
import { languages } from "../i18n";

function Controls() {
  const { mode, toggleMode, accent, setAccent, lang, setLang } = useSite();
  const [palOpen, setPalOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setPalOpen(false); setLangOpen(false); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const cur = languages.find((l) => l.code === lang) || languages[0];
  return (
    <div ref={ref} className="flex items-center gap-2">
      {/* language dropdown */}
      <div className="relative">
        <button
          onClick={() => { setLangOpen((o) => !o); setPalOpen(false); }}
          className="flex items-center gap-1.5 h-8 px-2.5 border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors"
          aria-label="Language"
        >
          <Globe className="w-3.5 h-3.5" />
          <span className="font-label text-[0.62rem] uppercase tracking-[0.12em]">{cur.label}</span>
        </button>
        {langOpen && (
          <div className="absolute right-0 mt-2 w-40 bg-ink2 border border-gold/40 py-1 z-50 max-h-[70vh] overflow-auto">
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => { setLang(l.code); setLangOpen(false); }}
                className={`flex items-center justify-between w-full px-3 py-2 text-left font-label text-[0.7rem] uppercase tracking-[0.1em] transition-colors ${lang === l.code ? "text-gold" : "text-stone2 hover:text-parchment"}`}
              >
                {l.name}
                {lang === l.code && <Check className="w-3.5 h-3.5" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* accent palette */}
      <div className="relative">
        <button
          onClick={() => { setPalOpen((o) => !o); setLangOpen(false); }}
          className="grid place-items-center w-8 h-8 border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors"
          aria-label="Accent colour"
        >
          <Palette className="w-3.5 h-3.5" />
        </button>
        {palOpen && (
          <div className="absolute right-0 mt-2 flex gap-2 bg-ink2 border border-gold/40 p-2.5 z-50">
            {ACCENTS.map((a) => (
              <button
                key={a.id}
                onClick={() => { setAccent(a.value); setPalOpen(false); }}
                title={a.name}
                className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${accent === a.value ? "border-parchment" : "border-transparent"}`}
                style={{ backgroundColor: a.value }}
              />
            ))}
          </div>
        )}
      </div>

      {/* light / dark */}
      <button
        onClick={toggleMode}
        className="grid place-items-center w-8 h-8 border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors"
        aria-label="Toggle light or dark"
      >
        {mode === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { c } = useSite();
  const { brand, navLinks, ui } = c;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 250);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };
  const goRoute = (path) => { setOpen(false); navigate(path); window.scrollTo(0, 0); };

  const solid = scrolled || location.pathname !== "/";
  const linkCls = "font-label text-[0.64rem] uppercase tracking-[0.18em] text-stone2 hover:text-gold transition-colors";
  const routeCls = (p) => `font-label text-[0.64rem] uppercase tracking-[0.18em] transition-colors ${location.pathname === p ? "text-gold" : "text-stone2 hover:text-gold"}`;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${solid ? "backdrop-blur-md border-b border-[var(--c-border)]" : "bg-transparent"}`}
      style={solid ? { backgroundColor: "var(--c-bg)" } : undefined}
    >
      <div className="max-w-[1220px] mx-auto px-4 sm:px-8">
        {/* Row 1: centered wordmark, controls on the right */}
        <div className="relative h-16 flex items-center">
          <button onClick={() => go("top")} className="flex items-center gap-2.5 group lg:absolute lg:left-1/2 lg:-translate-x-1/2">
            <Crest size={30} className="shrink-0 transition-transform duration-500 group-hover:rotate-3" />
            <span className="font-wordmark text-base sm:text-xl lg:text-2xl leading-none text-gold whitespace-nowrap">
              {brand.wordmark}
            </span>
          </button>

          <div className="ml-auto flex items-center gap-2">
            <div className="hidden lg:flex items-center gap-2"><Controls /></div>
            <button className="lg:hidden text-parchment p-1 shrink-0" onClick={() => setOpen((o) => !o)} aria-label="Menu">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Desktop link rows (split across two centered rows) — large screens only */}
        <div className="hidden lg:block pb-3 -mt-0.5">
          <nav className="flex flex-wrap items-center justify-center gap-x-7 gap-y-1.5">
            {navLinks.slice(0, 6).map((l) => (
              <button key={l.id} onClick={() => go(l.id)} className={linkCls}>{l.label}</button>
            ))}
          </nav>
          <nav className="flex flex-wrap items-center justify-center gap-x-7 gap-y-1.5 mt-2">
            {navLinks.slice(6).map((l) => (
              <button key={l.id} onClick={() => go(l.id)} className={linkCls}>{l.label}</button>
            ))}
            <button data-testid="desktop-nav-cave" onClick={() => goRoute("/cave")} className={routeCls("/cave")}>{ui.cave}</button>
            <button data-testid="desktop-nav-hez" onClick={() => goRoute("/hez")} className={routeCls("/hez")}>{ui.hez}</button>
            <button data-testid="desktop-nav-notable" onClick={() => goRoute("/notable")} className={routeCls("/notable")}>{ui.notable}</button>
            <button data-testid="desktop-nav-recipes" onClick={() => goRoute("/recipes")} className={routeCls("/recipes")}>{c.recipes.label}</button>
            <button data-testid="desktop-nav-guestbook" onClick={() => goRoute("/guestbook")} className={routeCls("/guestbook")}>{ui.guestbook}</button>
            <button data-testid="desktop-nav-contact" onClick={() => goRoute("/contact")} className={routeCls("/contact")}>{ui.contact || "Contact"}</button>
            <button data-testid="desktop-nav-press" onClick={() => goRoute("/press")} className={routeCls("/press")}>{ui.press}</button>
          </nav>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[var(--c-border)] px-4 py-3 max-h-[70vh] overflow-y-auto" style={{ backgroundColor: "var(--c-bg)" }}>
          <div className="flex justify-end pb-3 mb-2 border-b border-[var(--c-border)]">
            <Controls />
          </div>
          {navLinks.map((l) => (
            <button key={l.id} data-testid={`mobile-nav-${l.id}`} onClick={() => go(l.id)} className="block w-full text-left py-2.5 font-label text-xs uppercase tracking-[0.16em] text-stone2 hover:text-gold">
              {l.label}
            </button>
          ))}
          <button data-testid="mobile-nav-cave" onClick={() => goRoute("/cave")} className="block w-full text-left py-2.5 font-label text-xs uppercase tracking-[0.16em] text-stone2 hover:text-gold">
            {ui.cave}
          </button>
          <button data-testid="mobile-nav-notable" onClick={() => goRoute("/notable")} className="block w-full text-left py-2.5 font-label text-xs uppercase tracking-[0.16em] text-stone2 hover:text-gold">
            {ui.notable}
          </button>
          <button data-testid="mobile-nav-hez" onClick={() => goRoute("/hez")} className="block w-full text-left py-2.5 font-label text-xs uppercase tracking-[0.16em] text-stone2 hover:text-gold">
            {ui.hez}
          </button>
          <button data-testid="mobile-nav-recipes" onClick={() => goRoute("/recipes")} className="block w-full text-left py-2.5 font-label text-xs uppercase tracking-[0.16em] text-stone2 hover:text-gold">
            {c.recipes.label}
          </button>
          <button data-testid="mobile-nav-guestbook" onClick={() => goRoute("/guestbook")} className="block w-full text-left py-2.5 font-label text-xs uppercase tracking-[0.16em] text-stone2 hover:text-gold">
            {ui.guestbook}
          </button>
          <button data-testid="mobile-nav-contact" onClick={() => goRoute("/contact")} className="block w-full text-left py-2.5 font-label text-xs uppercase tracking-[0.16em] text-stone2 hover:text-gold">
            {ui.contact || "Contact"}
          </button>
          <button data-testid="mobile-nav-press" onClick={() => goRoute("/press")} className="block w-full text-left py-2.5 font-label text-xs uppercase tracking-[0.16em] text-stone2 hover:text-gold">
            {ui.press}
          </button>
        </div>
      )}
    </header>
  );
}
